import React, { createContext, useContext, useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from 'sonner';

// Pages
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import CustomerDashboard from './pages/CustomerDashboard';
import CarrierDashboard from './pages/CarrierDashboard';
import DriverDashboard from './pages/DriverDashboard';
import JobListings from './pages/JobListings';
import CarrierDirectory from './pages/CarrierDirectory';
import DriverJobBoard from './pages/DriverJobBoard';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionCancel from './pages/SubscriptionCancel';
import BankPayment from './pages/BankPayment';
import UserAgreement from './pages/UserAgreement';
import TermsOfService from './pages/TermsOfService';

// Components
import ChatWidget from './components/ChatWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// API instance with auth
export const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/auth';
    }
    return Promise.reject(error);
  }
);

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token is still valid
      apiClient.get('/auth/me')
        .then(res => {
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data));
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateUser = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

// Protected Route
function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  // Note: Subscription check is handled within dashboard components themselves
  // They show a subscription prompt when user.subscription_status !== 'active'
  // No redirect needed here - dashboards handle inactive subscriptions gracefully

  return children;
}

// Dashboard redirect based on role
function DashboardRedirect() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  switch (user.role) {
    case 'customer':
      return <Navigate to="/customer/dashboard" replace />;
    case 'carrier':
      return <Navigate to="/carrier/dashboard" replace />;
    case 'driver':
      return <Navigate to="/driver/dashboard" replace />;
    default:
      return <Navigate to="/" replace />;
  }
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/jobs" element={<JobListings />} />
          <Route path="/carriers" element={<CarrierDirectory />} />
          <Route path="/driver-jobs" element={<DriverJobBoard />} />
          
          {/* Dashboard redirect */}
          <Route path="/dashboard" element={<DashboardRedirect />} />
          
          {/* Customer routes */}
          <Route path="/customer/dashboard" element={
            <ProtectedRoute roles={['customer']}>
              <CustomerDashboard />
            </ProtectedRoute>
          } />
          
          {/* Carrier routes */}
          <Route path="/carrier/dashboard" element={
            <ProtectedRoute roles={['carrier']}>
              <CarrierDashboard />
            </ProtectedRoute>
          } />
          
          {/* Driver routes */}
          <Route path="/driver/dashboard" element={
            <ProtectedRoute roles={['driver']}>
              <DriverDashboard />
            </ProtectedRoute>
          } />
          
          {/* Subscription routes */}
          <Route path="/subscription/success" element={<SubscriptionSuccess />} />
          <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
          <Route path="/subscription/bank-payment" element={<BankPayment />} />
          
          {/* Legal */}
          <Route path="/user-agreement" element={<UserAgreement />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {/* Global Chat Widget */}
        <ChatWidgetWrapper />
      </BrowserRouter>
    </AuthProvider>
  );
}

// Wrapper to only show chat for authenticated users
function ChatWidgetWrapper() {
  const { user } = useAuth();
  const location = useLocation();
  
  // Don't show on auth pages or landing
  const hiddenPaths = ['/', '/auth'];
  if (hiddenPaths.includes(location.pathname) || !user || user.subscription_status !== 'active') {
    return null;
  }
  
  return <ChatWidget />;
}

export default App;
